import {
  Drawer,
  Button,
  Table,
  Spin,
  Alert,
  Tag,
  Typography,
} from "antd";
import {
  ReloadOutlined,
  CheckCircleOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import {
  drawerTitle,
  drawerFooter,
  DRAWER_WIDE_WIDTH,
} from "../../../../components";
import { usePurchasePaymentsQuery } from "../../../../Services";

const { Text } = Typography;

interface Payment {
  id: string;
  invoice_no: string;
  amount: number;
  payment_date: string;
  payment_method: string;
  status: string;
  created_at: string;
  purchase_id: string;
  supplier_name?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export const PaymentManageDrawer: React.FC<Props> = ({ open, onClose }) => {
  const {
    data: payments = [],
    isLoading,
    isError,
    isFetching,
    error,
    refetch,
  } = usePurchasePaymentsQuery({ page: 1, limit: 100 }, { enabled: open });

  const loading = isFetching;

  const columns = [
    {
      title: "Invoice",
      dataIndex: "invoice_no",
      key: "invoice_no",
      width: 130,
      render: (val: string) => (
        <Text className="font-mono text-xs">{val || "-"}</Text>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 110,
      render: (val: number) => (
        <Text className="font-semibold text-xs">
          LKR {val?.toLocaleString() || "0"}
        </Text>
      ),
    },
    {
      title: "Payment Date",
      dataIndex: "payment_date",
      key: "payment_date",
      width: 120,
      render: (val: string) => (val ? new Date(val).toLocaleDateString() : "-"),
    },
    {
      title: "Method",
      dataIndex: "payment_method",
      key: "payment_method",
      width: 100,
      render: (val: string) => <Tag className="text-[10px]">{val || "-"}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 90,
      render: (val: string) => (
        <Tag
          color={
            val === "paid" ? "green" : val === "partial" ? "orange" : "red"
          }
          className="text-[10px]"
        >
          {val}
        </Tag>
      ),
    },
  ];

  const errorMsg = isError
    ? (error as any)?.message || "Failed to load payments."
    : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={DRAWER_WIDE_WIDTH}
      title={drawerTitle(
        "Payment Management",
        "View purchase payments in the POS system",
      )}
      destroyOnClose
      footer={drawerFooter({ actions: [{ label: "Close", onClick: onClose }] })}
    >
      <div className="flex flex-col gap-4 h-full">
        {errorMsg && (
          <Alert
            message="Error"
            type="error"
            showIcon
            description={
              <div className="flex flex-col gap-2">
                <Text type="danger" className="text-xs">
                  {errorMsg}
                </Text>
                <Button
                  size="small"
                  danger
                  icon={<ReloadOutlined />}
                  onClick={() => refetch()}
                  className="w-fit"
                >
                  Retry
                </Button>
              </div>
            }
          />
        )}

        {!errorMsg && (
          <Alert
            type="success"
            showIcon={false}
            className="border-green-100 bg-green-50"
            message={
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-green-800 flex items-center gap-2">
                  <CheckCircleOutlined className="text-green-500" />
                  Connected &mdash; {payments.length} payment record(s)
                </span>
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => refetch()}
                  loading={loading}
                >
                  Refresh
                </Button>
              </div>
            }
          />
        )}

        <div className="flex-grow overflow-auto relative min-h-[200px]">
          {isLoading && (
            <div className="flex items-center justify-center h-40">
              <Spin tip="Loading payments..." size="large" />
            </div>
          )}

          <Table
            dataSource={payments}
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 15 }}
            loading={loading}
            locale={{ emptyText: "No payment records found." }}
          />

          {!isLoading && payments.length === 0 && (
            <div className="text-center text-gray-400 text-sm py-10">
              <div className="flex flex-col items-center gap-2">
                <DollarOutlined className="text-2xl text-gray-300" />
                <span>No payment records found.</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};
